import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Toaster } from 'react-hot-toast';
import Home from './pages/Home';
import TopBar from './components/TopBar';
import Header from './components/Header';
import Footer from './components/Footer';
import AdminLayout from './components/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';
import ChatbotWidget from './components/ChatbotWidget';
import MobileStickyCta from './components/MobileStickyCta';
import './index.css';

// Code splitting - Componentes carregados sob demanda
const Contact = lazy(() => import('./pages/Contact'));
const Sobre = lazy(() => import('./pages/Sobre'));
const Termos = lazy(() => import('./pages/Termos'));
const Privacidade = lazy(() => import('./pages/Privacidade'));
const TvPage = lazy(() => import('./servicos/TvPage'));
const NotebookPage = lazy(() => import('./servicos/NotebookPage'));
const PcPage = lazy(() => import('./servicos/PcPage'));
const MicroondasPage = lazy(() => import('./servicos/MicroondasPage'));
const NobreakPage = lazy(() => import('./servicos/NobreakPage'));
const OrcamentoGratis = lazy(() => import('./servicos/OrcamentoGratis'));
const ContratoPage = lazy(() => import('./servicos/ContratoPage'));
const AtendimentoRemotoPage = lazy(() => import('./servicos/AtendimentoRemotoPage'));
const VendaPecasPage = lazy(() => import('./servicos/VendaPecasPage'));
const LoginPage = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const FormularioChamado = lazy(() => import('./pages/FormularioChamado'));
const MonitorChamados = lazy(() => import('./pages/MonitorChamados'));
const CadastrarOrcamento = lazy(() => import('./pages/admin/CadastrarOrcamento'));
const OrcamentoDetalhes = lazy(() => import('./pages/OrcamentoDetalhes'));
const PainelLogs = lazy(() => import('./pages/admin/PainelLogs'));
const Usuarios = lazy(() => import('./pages/admin/Usuarios'));
const Mensagens = lazy(() => import('./pages/admin/Mensagens'));

// Componente de loading
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
  </div>
);

function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <>
      {/* Skip link para acessibilidade */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 
                   focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white 
                   focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        Pular para o conteúdo principal
      </a>

      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          duration: 5000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />      
      {!isAdminRoute && <TopBar />}
      {!isAdminRoute && <Header />}
      <main id="main-content" className={isAdminRoute ? '' : 'pt-4'} tabIndex={-1}>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/contato" element={<Contact />} />
            <Route path="/sobre" element={<Sobre />} />
            <Route path="/termos" element={<Termos />} />
            <Route path="/privacidade" element={<Privacidade />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:resettoken" element={<ResetPassword />} />

            <Route path="/servicos/notebook" element={<NotebookPage />} />
            <Route path="/servicos/pc" element={<PcPage />} />
            <Route path="/servicos/tv" element={<TvPage />} />
            <Route path="/servicos/microondas" element={<MicroondasPage />} />
            <Route path="/servicos/nobreaks" element={<NobreakPage />} />
            <Route path="/servicos/orcamento" element={<OrcamentoGratis />} />
            <Route path="/servicos/contrato" element={<ContratoPage />} />
            <Route path="/servicos/atendimento-remoto" element={<AtendimentoRemotoPage />} />
            <Route path="/servicos/venda-pecas" element={<VendaPecasPage />} />

            <Route path="/formulario-chamado" element={<FormularioChamado />} />
            <Route path="/monitor-chamados" element={<MonitorChamados />} />

            {/* Rotas Protegidas */}
            <Route element={<ProtectedRoute />}> 
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Navigate to="/admin/cadastrar-orcamento" replace />} />
                <Route path="cadastrar-orcamento" element={<CadastrarOrcamento />} />
                <Route path="painel-logs" element={<PainelLogs />} />
                <Route path="usuarios" element={<Usuarios />} />
                <Route path="mensagens" element={<Mensagens />} />
              </Route>
            </Route>
            
            <Route path="/orcamento/:numeroOrcamentoEncoded" element={<OrcamentoDetalhes />} />
          </Routes>
        </Suspense>
      </main> 
      {!isAdminRoute && <ChatbotWidget />}
      {!isAdminRoute && <MobileStickyCta />}
      {!isAdminRoute && <Footer />}
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
